//---- React
import { useQuery } from "@tanstack/react-query";
//---- Ant Design
import { Table, Modal } from "antd";

//---- Project related
import { getEOLData } from "apis/eolApis";
import { formatOnlyTime } from "components/Common/Utils";
import APIError from "components/Common/APIError";
import Loading from "components/Common/Loading";
import NoData from "components/Common/NoData";

const HBDataGrid = ({
  dataModalOpen,
  vIdVal,
  selectedHBTestId,
  dataTable,
  field,
  showErrors,
  onCloseDataLink,
}) => {
  const {
    data: gridData,
    isLoading: gridLoading,
    isError: gridIsError,
    error: gridError,
  } = useQuery({
    queryKey: ["hbdatagrid", vIdVal, selectedHBTestId],
    queryFn: () =>
      getEOLData(vIdVal, selectedHBTestId, dataTable, field, showErrors),
    keepPreviousData: true,
    enabled: !!(vIdVal && selectedHBTestId && dataTable),
  });

  const { data, error } = gridData || [];
  const mergeData = () => {
    const errorMap = error.reduce((acc, item) => {
      acc[item.timestamp] = item;
      return acc;
    }, {});

    const mergedData = data.map((dataRec) => {
      let errorRec = errorMap[dataRec.timestamp];
      if (errorRec) {
        Object.keys(dataRec).forEach((key) => {
          if (key !== "imei" || key !== "timestamp") {
            if (dataRec[key] === null && errorRec[key] !== null) {
              dataRec[key] = errorRec[key];
            }
          }
        });
      }
      return dataRec;
    });
    return mergedData;
  };

  const formatColumns = () => {
    const errorMap = error.reduce((acc, item) => {
      acc[item.timestamp] = item;
      return acc;
    }, {});
    const fields = Object.keys(data?.[0])
      .filter((field) => field !== "imei" && field !== "timestamp")
      .sort();
    const columns = [
      {
        title: "timestamp",
        dataIndex: "timestamp",
        key: "timestamp",
        align: "left",
        width: 130,
      },
    ];
      return columns.concat(
        fields.map((field) => {
          return {
            title: field,
            dataIndex: field,
            key: field,
            align: "left",
            width: 130,
            render: (text, record) => (
              <span
                style={{
                  color:
                    errorMap[record.timestamp] &&
                    errorMap[record.timestamp][field] !== null
                      ? "red"
                      : "",
                }}
              >
                {text}
              </span>
            ),
          };
        })
      );
    };
  
    const formatData = () => {
      const data = mergeData();
      const formattedData = data?.map((dataItem, index) => {
        let rowObj = {};
        Object.keys(dataItem).forEach((key) => {
          key === 'timestamp' ?
          rowObj = { ...rowObj, [key]: formatOnlyTime(dataItem[key]) }
          :
          rowObj = { ...rowObj, [key]: dataItem[key] !== null ? dataItem[key]?.toString() : dataItem[key] }
      });
      return rowObj;
    });
    return formattedData;
  };

  return (
    <Modal
      title={
        dataTable.charAt(0).toUpperCase() +
        dataTable.slice(1).toLowerCase() +
        " Data"
      }
      open={dataModalOpen}
      closable={true}
      onCancel={onCloseDataLink}
      rowKey="key"
      loading={gridLoading}
      footer={null}
      width={{
        xs: "90%",
        sm: "80%",
        md: "75%",
        lg: "80%",
        xl: "90%",
        xxl: "90%",
      }}
    >
      {gridLoading && <Loading />}
      {gridIsError && <APIError message={gridError?.message} />}
      {!gridLoading && gridData?.data?.length === 0 && <NoData />}
      {!gridIsError && gridData?.data?.length > 0 && (
        <>
          {showErrors && (
            <div>
              <p>
                <b>Missing Data:</b>{" "}
                <span>{gridData?.missingField?.join(", ")}</span>
              </p>
              <p>
                <b>Incorrect Data:</b> {gridData?.errorField?.join(", ")}
              </p>
            </div>
          )}
          <div>
            <Table
              dataSource={formatData()}
              columns={formatColumns()}
              pagination={false}
              footer={null}
              scroll={{ y: 500, x: "max-content" }}
            />
          </div>
        </>
      )}
    </Modal>
  );
};

export default HBDataGrid;
